import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import EmailIcon from '../../../../../public/static/icons/letter-icon.svg';
import ProfileIcon from '../../../../../public/static/icons/profile-icon.svg';
import PhoneIcon from '../../../../../public/static/icons/phone-2024.svg';
import { setFocusById } from '../../../../../utils/donationForm';
import {
  composeParsers,
  parseOnlyDigits,
  parsePhone,
  parseLengthLimit,
  parseSpaceless,
} from '../../../../../utils/formFieldParsers';
import {
  composeValidators,
  required,
  minValue,
  validEmail,
  minDigits,
  maxDigits,
} from '../../../../../utils/formFieldValidators';
import { SD, RG, DEFAULT_COUNTRY_CODE } from '../../../../../utils/constants';
import { DEFAULT_CURRENCY, currencySymbolPath, getSymbol } from '../../../../../utils/currency';
import { TextField } from '../../../../01_atoms/FormElements';
import FormattedText from '../../../../01_atoms/FormattedText';
import Company from '../../../../02_molecules/Company';

const AmountPersonalInfoStep = ({
  heading = '',
  text = '',
  minAmount,
  donationType,
  email = '',
  name = '',
  extraFeatures,
  values = {},
  showCompanyDonation = false,
}) => {
  useEffect(() => {
    if (!values.amount) {
      setFocusById('amount');
    } else if (extraFeatures.includes('school')) {
      setFocusById('school');
    } else {
      setFocusById('first_name');
    }
  }, []);

  return (
    <>
      {heading && <h2>{heading}</h2>}
      {text && <FormattedText text={text} />}

      <div className={`form-field-with-description${email ? ' shortform' : ''}`}>
        <TextField
          name="amount"
          label={donationType === RG ? 'Your monthly donation' : 'Your donation'}
          parse={composeParsers(parseOnlyDigits, parseLengthLimit(6))}
          validate={composeValidators(
            required('Please add a donation amount'),
            minValue(minAmount, `Please enter an amount greater than ${minAmount - 1}`),
          )}
          isRequired
          icon={currencySymbolPath[DEFAULT_CURRENCY]}
          autoComplete="off"
          textIcon={donationType === RG ? 'Monthly' : ''}
          inputMode="numeric"
          pattern="[0-9]+"
          placeholder=" "
          aria-label={getSymbol(DEFAULT_CURRENCY)}
          infoOnPatternNotMatched="Please use numbers only"
        />
      </div>

      {/* We display a shortform if we know the donor's email and name,
     otherwise we display the default fields
     */}
      {!email || !name ? (
        <>
          {extraFeatures.includes('school') && (
            <TextField
              name="school_name"
              label="School number"
              validate={required('Please enter your school number')}
              isRequired
              placeholder=" "
            />
          )}

          <div className="row">
            <div className="col-sm pr-sm-2 ml-md-0 mb-0">
              <TextField
                name="first_name"
                label="First name"
                validate={required('Please enter your first name')}
                isRequired
                placeholder="Jane"
                icon={<ProfileIcon />}
              />
            </div>
            <div className="col-sm pl-sm-2 mr-md-0 mb-0">
              <TextField
                name="last_name"
                label="Last name"
                validate={required('Please enter your last name')}
                isRequired
                placeholder="Bloggs"
              />
            </div>
          </div>
          <TextField
            name="email"
            label="Email address"
            placeholder="jane@email.com"
            parse={parseSpaceless}
            validate={composeValidators(required('Please enter your email address'), validEmail())}
            isRequired
            tooltip={
              DEFAULT_COUNTRY_CODE === 'GB'
                ? 'This lets us send you a receipt for your donation.'
                : 'This is in case we need to contact you about your donation.'
            }
            icon={<EmailIcon />}
          />

          <TextField
            name="phone"
            label="Phone number"
            placeholder={DEFAULT_COUNTRY_CODE === 'GB' ? '+44 1 000 0000' : '+353 1 000 0000'}
            parse={parsePhone}
            validate={
              DEFAULT_COUNTRY_CODE === 'GB'
                ? minDigits(7, 'Please enter a valid phone number with at least 7 digits')
                : composeValidators(
                    required('Please enter a phone number in order to proceed'),
                    minDigits(7, 'Please enter a valid phone number with at least 7 digits'),
                    maxDigits(
                      18,
                      'Please enter a valid phone number with no longer than 18 digits',
                    ),
                  )
            }
            isRequired={DEFAULT_COUNTRY_CODE !== 'GB'}
            tooltip={
              DEFAULT_COUNTRY_CODE === 'GB'
                ? undefined
                : 'This is in case we need to contact you about your donation.'
            }
            icon={<PhoneIcon />}
            inputMode="numeric"
            pattern="[0-9+\s\-\(\)\.]+"
            infoOnPatternNotMatched="Please use numbers only"
          />
          {showCompanyDonation && <Company />}
        </>
      ) : (
        <>
          <div className="amount-short-step-details">
            <h2>My details</h2>
            <div id="donation-short-form-name" data-hj-masked="">
              {name}
            </div>
            <div id="donation-short-form-email" data-hj-masked="">
              {email}
            </div>
          </div>

          <a
            className="this-is-not-me"
            // Redirect user to the same form step, but without query params
            // to show all fields instead of shortform fields only.
            onClick={() => {
              window.location = window.location.pathname;
            }}
            onKeyDown={() => {
              window.location = window.location.pathname;
            }}
          >
            This is not me
          </a>
        </>
      )}
    </>
  );
};

AmountPersonalInfoStep.propTypes = {
  heading: PropTypes.string,
  text: PropTypes.string,
  minAmount: PropTypes.number.isRequired,
  donationType: PropTypes.oneOf([RG, SD]).isRequired,
  email: PropTypes.string,
  name: PropTypes.string,
  extraFeatures: PropTypes.arrayOf(PropTypes.string).isRequired,
  values: PropTypes.shape(),
  showCompanyDonation: PropTypes.bool,
};

export default AmountPersonalInfoStep;
